/**
 * FloatingObjectsLibrary.ts - manifest loader for static floating objects.
 *
 * These are not player-selectable boat designs. They are simple harbour
 * occupants: one visual GLB, no moving parts, no gameplay markers, with a
 * coarse solid collision body derived from the rendered mesh bounds.
 */

export type FloatingObjectCategory =
  | "sailboat"
  | "motorboat"
  | "workboat"
  | "barge"
  | "buoy"
  | "other";

export type FloatingObjectAxis = "X" | "Y" | "Z";

export interface FloatingObjectRenderSpec {
  /** Authored up axis in the GLB. Defaults to Y. */
  axisUp?: FloatingObjectAxis;
  /** Authored forward/length axis in the GLB. Defaults to Z. */
  axisLength?: FloatingObjectAxis;
  /**
   * How the mesh is vertically placed on the water plane.
   * raw = source Y=0 is the authored waterline; bbox-bottom = lowest vertex
   * touches the water. Floating boats should normally use raw.
   */
  yAlign?: "raw" | "bbox-bottom";
  /** Extra vertical offset in metres after yAlign is applied. */
  yOffset?: number;
  /** Extra yaw applied after axis conversion, in degrees. */
  yawDeg?: number;
  /** Uniform scale multiplier applied after any future auto-sizing. */
  scale?: number;
  castsShadow?: boolean;
  receivesShadow?: boolean;
}

export type FloatingObjectCollisionType = "mesh-aabb" | "none";

export interface FloatingObjectCollisionSpec {
  /** mesh-aabb = use the loaded GLB bounds as the solid collision volume. */
  type: FloatingObjectCollisionType;
  solid?: boolean;
  /** Optional expansion around the generated collision body, in metres. */
  paddingM?: number;
}

export interface FloatingObjectFeatureFlags {
  playerSelectable?: boolean;
  animatedParts?: boolean;
  markers?: boolean;
}

export interface FloatingObjectEntry {
  id: string;
  name: string;
  category: FloatingObjectCategory;
  /** Path relative to public root, e.g. "floating-objects/yacht_small.glb". */
  url: string;
  render?: FloatingObjectRenderSpec;
  collision: FloatingObjectCollisionSpec;
  features?: FloatingObjectFeatureFlags;
}

/** Schema of public/floating-objects/index.json. */
export interface FloatingObjectsIndex {
  version: 1;
  floatingObjects: FloatingObjectEntry[];
}

export class FloatingObjectsLibrary {
  private readonly entries = new Map<string, FloatingObjectEntry>();

  private constructor(entries: FloatingObjectEntry[]) {
    for (const entry of entries) this.entries.set(entry.id, entry);
  }

  static async load(
    indexUrl = "floating-objects/index.json",
  ): Promise<FloatingObjectsLibrary> {
    const idx = await fetchJson<FloatingObjectsIndex>(indexUrl);
    validateIndex(idx, indexUrl);
    return new FloatingObjectsLibrary(idx.floatingObjects);
  }

  get(id: string): FloatingObjectEntry {
    const entry = this.entries.get(id);
    if (!entry) {
      throw new Error(
        `FloatingObjectsLibrary: unknown object id "${id}". Known: ${this.ids().join(", ")}`,
      );
    }
    return entry;
  }

  ids(): string[] {
    return [...this.entries.keys()];
  }

  list(): FloatingObjectEntry[] {
    return [...this.entries.values()];
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`fetch ${url} -> HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function validateIndex(
  idx: unknown,
  indexUrl: string,
): asserts idx is FloatingObjectsIndex {
  if (!idx || typeof idx !== "object") {
    throw new Error(`FloatingObjectsLibrary: malformed index at ${indexUrl}`);
  }
  const value = idx as Record<string, unknown>;
  if (value.version !== 1 || !Array.isArray(value.floatingObjects)) {
    throw new Error(`FloatingObjectsLibrary: malformed index at ${indexUrl}`);
  }
  const seen = new Set<string>();
  for (const entry of value.floatingObjects) {
    validateEntry(entry, indexUrl);
    if (seen.has(entry.id)) {
      throw new Error(
        `FloatingObjectsLibrary: duplicate object id "${entry.id}" in ${indexUrl}`,
      );
    }
    seen.add(entry.id);
  }
}

function validateEntry(
  entry: unknown,
  indexUrl: string,
): asserts entry is FloatingObjectEntry {
  if (!entry || typeof entry !== "object") {
    throw new Error(`FloatingObjectsLibrary: malformed entry in ${indexUrl}`);
  }
  const value = entry as Record<string, unknown>;
  const id = value.id;
  if (typeof id !== "string" || id.length === 0) {
    throw new Error(`FloatingObjectsLibrary: missing object id in ${indexUrl}`);
  }
  if (typeof value.name !== "string" || value.name.length === 0) {
    throw new Error(`FloatingObjectsLibrary[${id}]: missing name`);
  }
  if (!isCategory(value.category)) {
    throw new Error(`FloatingObjectsLibrary[${id}]: invalid category`);
  }
  if (typeof value.url !== "string" || !/\.glb$/i.test(value.url)) {
    throw new Error(`FloatingObjectsLibrary[${id}]: url must point to a .glb`);
  }
  if (!value.collision || typeof value.collision !== "object") {
    throw new Error(`FloatingObjectsLibrary[${id}]: missing collision spec`);
  }
  const collision = value.collision as Record<string, unknown>;
  if (collision.type !== "mesh-aabb" && collision.type !== "none") {
    throw new Error(`FloatingObjectsLibrary[${id}]: invalid collision type`);
  }
}

function isCategory(value: unknown): value is FloatingObjectCategory {
  return (
    value === "sailboat" ||
    value === "motorboat" ||
    value === "workboat" ||
    value === "barge" ||
    value === "buoy" ||
    value === "other"
  );
}
